<template>
  <Dialog
    :model-value="visible"
    title="Manage Workflow Usage"
    dialog-type="primary"
    dialog-size="small"
    :close-on-overlay="false"
    :header-icon-src="require('@/assets/MyPatient/document_white.svg')"
    @update:model-value="(v) => { if (!v) closeDialog() }"
    @close="closeDialog"
  >
    <div class="dialog-content">
      <!-- Current Workflow Status -->
      <div class="status-section">
        <p class="section-title">Current Workflow Status</p>
        <div class="workflow-toggle">
          <span class="toggle-label" :class="{ disabled: isSwitcherDisabled }">Draft</span>
          <el-tooltip
            :content="disabledTooltipMessage"
            :disabled="!isSwitcherDisabled"
            placement="top"
            effect="dark"
          >
            <div 
              class="toggle-switch" 
              :class="{ active: isActive, disabled: isSwitcherDisabled }" 
              @click="isSwitcherDisabled ? null : toggleStatus()"
            >
              <div class="toggle-handle"></div>
            </div>
          </el-tooltip>
          <span class="toggle-label" :class="{ disabled: isSwitcherDisabled }">Active</span>
        </div>
      </div>

      <!-- Draft Placeholder - Shown when workflow is draft -->
      <div v-if="!isActive" class="draft-placeholder">
        <div class="placeholder-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
            <path d="M24 4C12.96 4 4 12.96 4 24C4 35.04 12.96 44 24 44C35.04 44 44 35.04 44 24C44 12.96 35.04 4 24 4ZM24 40C15.18 40 8 32.82 8 24C8 15.18 15.18 8 24 8C32.82 8 40 15.18 40 24C40 32.82 32.82 40 24 40ZM22 22V14H26V22H22ZM22 34V26H26V34H22Z" fill="#9CA3AF"/>
          </svg>
        </div>
        <p class="placeholder-title">Assignee Configuration Unavailable</p>
        <p class="placeholder-description">
          To configure patient assignments, please activate this workflow by toggling the switch above to "Active".
        </p>
      </div>

      <!-- Assigned Patients - Only shown when workflow is active -->
      <div v-if="isActive" class="patients-section">
        <div class="patients-header">
          <p class="section-title">Assigned Patients</p>
          <button class="add-assignee-button" @click="addNewAssignee">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 12C17.21 12 19 10.21 19 8C19 5.79 17.21 4 15 4C12.79 4 11 5.79 11 8C11 10.21 12.79 12 15 12ZM6 10V7H4V10H1V12H4V15H6V12H9V10H6ZM15 14C12.33 14 7 15.34 7 18V20H23V18C23 15.34 17.67 14 15 14Z" fill="#374957"/>
            </svg>
            <span>Add New Assignee</span>
          </button>
        </div>

        <!-- Patient List -->
        <div class="patient-list">
          <!-- Empty State -->
          <div v-if="patients.length === 0" class="empty-state">
            <div class="empty-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                <path d="M32 8C18.7467 8 8 18.7467 8 32C8 45.2533 18.7467 56 32 56C45.2533 56 56 45.2533 56 32C56 18.7467 45.2533 8 32 8ZM32 13.3333C42.2133 13.3333 50.6667 21.7867 50.6667 32C50.6667 42.2133 42.2133 50.6667 32 50.6667C21.7867 50.6667 13.3333 42.2133 13.3333 32C13.3333 21.7867 21.7867 13.3333 32 13.3333ZM29.3333 21.3333V34.6667H42.6667V29.3333H34.6667V21.3333H29.3333Z" fill="#C9C9C9"/>
              </svg>
            </div>
            <p class="empty-title">No Assignees Yet</p>
            <p class="empty-description">
              Click "Add New Assignee" to assign patients to this workflow.
            </p>
          </div>

          <!-- Patient List -->
          <transition-group v-else name="patient-list" tag="div" class="patient-list-container">
            <div v-for="patient in patients" :key="patient.id" class="patient-item">
            <div class="patient-avatar">
              <img v-if="patient.avatar" :src="patient.avatar" :alt="patient.name" />
              <div v-else class="avatar-placeholder">{{ patient.initials }}</div>
            </div>
            <div class="patient-info">
              <p class="patient-name">{{ patient.name }}</p>
              <div class="patient-meta">
                <span class="patient-id">ID: #{{ patient.id }}</span>
                <span class="patient-added">Added {{ patient.addedTime }}</span>
              </div>
            </div>
            <div class="patient-actions">
              <FoldableButton
                :text="patient.isPaused ? 'Resume Usage' : 'Pause Usage'"
                built_in_theme="default"
                @click="togglePause(patient)"
              >
                <template #prefix>
                  <svg v-if="patient.isPaused" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M9 0C4.03125 0 0 4.03125 0 9C0 13.9688 4.03125 18 9 18C13.9688 18 18 13.9688 18 9C18 4.03125 13.9688 0 9 0ZM9 16.3125C4.96875 16.3125 1.6875 13.0312 1.6875 9C1.6875 4.96875 4.96875 1.6875 9 1.6875C13.0312 1.6875 16.3125 4.96875 16.3125 9C16.3125 13.0312 13.0312 16.3125 9 16.3125ZM6.75 5.0625V12.9375L13.5 9L6.75 5.0625Z" fill="currentColor"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M9 0C4.03125 0 0 4.03125 0 9C0 13.9688 4.03125 18 9 18C13.9688 18 18 13.9688 18 9C18 4.03125 13.9688 0 9 0ZM9 16.3125C4.96875 16.3125 1.6875 13.0312 1.6875 9C1.6875 4.96875 4.96875 1.6875 9 1.6875C13.0312 1.6875 16.3125 4.96875 16.3125 9C16.3125 13.0312 13.0312 16.3125 9 16.3125ZM7.3125 5.625H8.4375V12.375H7.3125V5.625ZM9.5625 5.625H10.6875V12.375H9.5625V5.625Z" fill="currentColor"/>
                  </svg>
                </template>
              </FoldableButton>
              <FoldableButton text="Remove Patient" built_in_theme="red" @click="deletePatient(patient)">
                <template #prefix>
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <path d="M13 1.30833L11.6917 0L6.5 5.19167L1.30833 0L0 1.30833L5.19167 6.5L0 11.6917L1.30833 13L6.5 7.80833L11.6917 13L13 11.6917L7.80833 6.5L13 1.30833Z" fill="currentColor"/>
                  </svg>
                </template>
              </FoldableButton>
            </div>
            </div>
          </transition-group>
        </div>
      </div>
    </div>

    <template #footer>
      <ResponsiveButton button_type="outlined" built_in_theme="error" size="medium" @click="closeDialog">Cancel</ResponsiveButton>
    </template>
  </Dialog>

  <!-- Add New Assignee Dialog -->
  <AddNewAssigneeDialog
    :visible="showAddAssigneeDialog"
    :workflow-id="workflowId"
    :existing-patient-ids="existingPatientIds"
    :apply-to-new-patients="applyToNewPatients"
    :is-program="false"
    @close="showAddAssigneeDialog = false"
    @success="handleAddAssigneesSuccess"
  />
</template>

<script>
import AddNewAssigneeDialog from './AddNewAssigneeDialog.vue';
import { notify } from '@/utils/notify';

export default {
  name: 'ManageUsageDialog',
  components: {
    AddNewAssigneeDialog
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    workflowId: {
      type: String,
      required: true
    },
    workflowStatus: {
      type: String,
      default: 'draft' // 'draft' or 'active'
    }
  },
  emits: ['close', 'save', 'status-changed', 'save-workflow-before-activate'],
  data() {
    return {
      isActive: false,
      showAddAssigneeDialog: false,
      isLoadingAssignees: false,
      patients: [],
      originalPatients: [], // Track original state to detect staged changes
      applyToNewPatients: false,
      originalApplyToNewPatients: false // Track original state
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    },
    existingPatientIds() {
      return this.patients.map(p => p.id);
    },
    hasStagedChanges() {
      // Check if there are any unsaved changes
      if (this.patients.length !== this.originalPatients.length) {
        return true;
      }
      
      // Check if applyToNewPatients changed
      if (this.applyToNewPatients !== this.originalApplyToNewPatients) {
        return true;
      }
      
      // Check if any patient was modified (paused status or removed)
      for (let i = 0; i < this.patients.length; i++) {
        const current = this.patients[i];
        const original = this.originalPatients.find(p => p.id === current.id);
        
        if (!original) {
          return true; // New patient added
        }
        
        if (current.isPaused !== original.isPaused) {
          return true; // Pause status changed
        }
      }
      
      // Check if any original patient was removed
      for (let i = 0; i < this.originalPatients.length; i++) {
        const original = this.originalPatients[i];
        const current = this.patients.find(p => p.id === original.id);
        
        if (!current) {
          return true; // Patient removed
        }
      }
      
      return false;
    },
    isSwitcherDisabled() {
      // Disable switcher if there are assigned patients
      // No need to check hasStagedChanges since we use real-time APIs
      return this.patients.length > 0;
    },
    disabledTooltipMessage() {
      // Generate tooltip message based on why switcher is disabled
      if (this.patients.length > 0) {
        return 'Cannot change status: workflow has assigned patients. Remove all patients before changing to Draft.';
      }
      return '';
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Initialize status from prop when dialog opens
        this.isActive = this.workflowStatus === 'active';
        
        // Load assignees if workflow is active
        if (this.isActive) {
          this.loadAssignees();
        }
      }
    }
  },
  mounted() {
    console.log('ManageUsageDialog mounted');
  },
  methods: {
    closeDialog() {
      this.$emit('close');
    },
    
    /**
     * Toggle workflow status between Draft and Active
     * Calls validate+activate or deactivate API immediately
     */
    async toggleStatus() {
      const newStatus = !this.isActive;
      
      if (newStatus) {
        // Switching from draft to active: validate first, then activate
        await this.activateWorkflow();
      } else {
        // Switching from active to draft: check if workflow has assigned patients
        if (this.patients.length > 0) {
          notify({
            title: 'Cannot Deactivate',
            message: 'Cannot change workflow status to Draft while it has assigned patients. Please remove all assigned patients first.',
            type: 'warning',
            showClose: true
          });
          return;
        }
        
        // Deactivate workflow
        await this.deactivateWorkflow();
      }
    },
    
    /**
     * Validate and activate workflow
     * First saves the workflow, then validates, then activates
     */
    async activateWorkflow() {
      try {
        // Step 1: Request workflow save from parent (WorkflowCanvas)
        // This ensures all unsaved changes are persisted before validation
        const savePromise = new Promise((resolve, reject) => {
          // Emit event to parent to save workflow
          this.$emit('save-workflow-before-activate', {
            onSuccess: () => resolve(true),
            onError: (error) => reject(error)
          });
        });

        // Wait for save to complete
        await savePromise;
        console.log('Workflow saved successfully before activation');

        // Step 2: Import API functions
        const { validateWorkflowApi, activateWorkflowApi } = await import('@/api/workflowManage');

        // Step 3: Validate workflow
        const validateResponse = await validateWorkflowApi(this.workflowId);
        
        // Status code 200 means validation passed
        // If status is not 200, validation failed
        if (validateResponse.status !== 200) {
          // Validation failed - show errors
          const validationData = validateResponse.data || validateResponse;
          let errorMessages = '';
          const errorList = validationData.errors || [];
          errorList.forEach(e=>{
            errorMessages += e.message + '\n';
          })
          notify({
            title: 'Validation Failed',
            message: errorMessages || 'Workflow validation failed',
            type: 'error',
            showClose: true
          });
          return;
        }
        
        // Show warnings if any
        const validationData = validateResponse.data || validateResponse;
        if (validationData.warnings && validationData.warnings.length > 0) {
          const warningMessages = validationData.warnings.join('\n');
          notify({
            title: 'Validation Warnings',
            message: warningMessages,
            type: 'warning',
            showClose: true
          });
        }
        
        // Step 2: Activate workflow
        const activateResponse = await activateWorkflowApi(this.workflowId);
        
        // Update local state
        this.isActive = true;
        
        // Emit status change to parent
        this.$emit('status-changed', { status: 'active', data: activateResponse.data });
        
        notify({
          title: 'Success',
          message: 'Workflow activated successfully!',
          type: 'success',
          showClose: true
        });
        
        // Load assignees for active workflow
        await this.loadAssignees();
        
      } catch (error) {
        console.error('Error activating workflow:', error);
        notify({
          title: 'Error',
          message: `Failed to activate workflow: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    },
    
    /**
     * Deactivate workflow
     */
    async deactivateWorkflow() {
      try {
        // Import API functions
        const { deactivateWorkflowApi } = await import('@/api/workflowManage');
        
        console.log('Deactivating workflow:', this.workflowId);
        const response = await deactivateWorkflowApi(this.workflowId);
        console.log('Deactivation response:', response);
        
        // Update local state
        this.isActive = false;
        
        // Emit status change to parent
        this.$emit('status-changed', { status: 'draft', data: response.data });
        
        notify({
          title: 'Success',
          message: 'Workflow deactivated successfully!',
          type: 'success',
          showClose: true
        });
        
      } catch (error) {
        console.error('Error deactivating workflow:', error);
        notify({
          title: 'Error',
          message: `Failed to deactivate workflow: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    },
    
    /**
     * Load assignees from API when workflow is active
     */
    async loadAssignees() {
      if (!this.isActive) return;
      
      try {
        this.isLoadingAssignees = true;
        const { getWorkflowAssigneesApi } = await import('@/api/workflowManage');
        
        console.log('Loading assignees for workflow:', this.workflowId);
        const response = await getWorkflowAssigneesApi(this.workflowId);
        console.log('Assignees response:', response);
        
        // Transform API response to component data structure
        const assigneesData = response.data || response;
        const assignments = assigneesData.assignments || [];
        
        // Get apply_to_new_patients setting from response
        this.applyToNewPatients = assigneesData.apply_to_new_patients || false;
        
        this.patients = assignments.map(a => {
          // Generate initials from patient name
          const nameParts = (a.patient_name || '').split(' ').filter(n => n);
          const initials = nameParts.map(n => n[0].toUpperCase()).join('');
          
          // Format assigned_at time to relative time
          const addedTime = this.formatRelativeTime(a.assigned_at);
          
          return {
            id: String(a.patient_id),
            name: a.patient_name || 'Unknown Patient',
            avatar: null,
            initials: initials || 'NA',
            addedTime: addedTime,
            isPaused: a.status === 'paused'
          };
        });
        
        // Store original state for change detection
        this.originalPatients = JSON.parse(JSON.stringify(this.patients));
        this.originalApplyToNewPatients = this.applyToNewPatients;
        
        console.log('Loaded patients:', this.patients);
        
      } catch (error) {
        console.error('Error loading assignees:', error);
        notify({
          title: 'Error',
          message: `Failed to load assignees: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      } finally {
        this.isLoadingAssignees = false;
      }
    },
    
    /**
     * Open dialog to add new assignees
     * LOCAL ONLY - No API call until Save Changes is clicked
     */
    addNewAssignee() {
      this.showAddAssigneeDialog = true;
    },
    
    /**
     * Format timestamp to relative time (e.g., "2 days ago")
     */
    formatRelativeTime(timestamp) {
      if (!timestamp) return 'Recently';
      
      const now = new Date();
      const date = new Date(timestamp);
      const diffMs = now - date;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      const diffWeeks = Math.floor(diffDays / 7);
      
      if (diffSecs < 60) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    },
    
    /**
     * Handle successful patient assignment from AddNewAssigneeDialog
     * API call is now handled in AddNewAssigneeDialog, this just updates local state
     */
    async handleAddAssigneesSuccess(data) {
      try {
        // Update apply to new patients setting
        if (data.autoApplyToNew !== undefined) {
          this.applyToNewPatients = data.autoApplyToNew;
        }
        
        // Close the add assignee dialog
        this.showAddAssigneeDialog = false;
        
        // Reload the assignees list to get the updated data from server
        await this.loadAssignees();
        
        // Emit save event to parent for dynamic tracking
        this.$emit('save', {
          isActive: this.isActive,
          patients: this.patients
        });
      } catch (error) {
        console.error('Error reloading patients:', error);
        notify({
          title: 'Error',
          message: `Failed to reload patients: ${error.message}`,
          type: 'error',
          showClose: true
        });
      }
    },
    
    /**
     * Toggle patient pause/resume status
     * Calls API immediately for real-time update
     */
    async togglePause(patient) {
      try {
        const { pausePatientAssignmentApi, resumePatientAssignmentApi } = await import('@/api/workflowManage');
        
        if (patient.isPaused) {
          // Resume the patient
          await resumePatientAssignmentApi(this.workflowId, patient.id);
          patient.isPaused = false;
          patient.status = 'running'; // Update to running status
          
          notify({
            title: 'Success',
            message: `Patient ${patient.name} resumed successfully`,
            type: 'success',
            showClose: true
          });
        } else {
          // Pause the patient
          await pausePatientAssignmentApi(this.workflowId, patient.id);
          patient.isPaused = true;
          patient.status = 'paused';
          
          notify({
            title: 'Success',
            message: `Patient ${patient.name} paused successfully`,
            type: 'success',
            showClose: true
          });
        }
        
        // Emit save event to parent for dynamic tracking
        this.$emit('save', {
          isActive: this.isActive,
          patients: this.patients
        });
        
      } catch (error) {
        console.error('Error toggling pause status:', error);
        notify({
          title: 'Error',
          message: `Failed to ${patient.isPaused ? 'resume' : 'pause'} patient: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    },
    
    /**
     * Remove patient from workflow
     * Calls API immediately for real-time removal
     */
    async deletePatient(patient) {
      try {
        const { unassignPatientFromWorkflowApi } = await import('@/api/workflowManage');
        
        await unassignPatientFromWorkflowApi(this.workflowId, patient.id);
        
        // Remove from local list
        const index = this.patients.findIndex(p => p.id === patient.id);
        if (index > -1) {
          this.patients.splice(index, 1);
        }
        
        notify({
          title: 'Success',
          message: `Patient ${patient.name} removed successfully`,
          type: 'success',
          showClose: true
        });
        
        // Emit save event to parent for dynamic tracking
        this.$emit('save', {
          isActive: this.isActive,
          patients: this.patients
        });
        
      } catch (error) {
        console.error('Error removing patient:', error);
        notify({
          title: 'Error',
          message: `Failed to remove patient: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    },
  }
};
</script>

<style scoped>
/* Header */
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #004879;
  border-radius: 10px 10px 0 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 4px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.header-title {
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: normal;
  color: #FFFFFF;
}

.close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.close-button:hover {
  opacity: 0.8;
}

/* Content */
.dialog-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 8px 8px;
  background: #F9FBFD;
}

/* Status Section */
.status-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 16px 12px;
  border-bottom: 1px dashed var(--DividerGray, #C4C4C4);
}

.section-title {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #4A4A4A;
  margin: 0;
}

.workflow-toggle {
  display: flex;
  align-items: center;
  gap: 7px;
}

.toggle-label {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666666;
  transition: color 0.3s ease;
}

.toggle-label.disabled {
  color: #C9C9C9;
}

.toggle-switch {
  position: relative;
  width: 40px;
  height: 22px;
  background: #F9FBFD;
  border: 1px solid #374957;
  border-radius: 13px;
  cursor: pointer;
  padding: 2px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
}

.toggle-switch.active {
  background: #004879;
  border-color: #004879;
}

.toggle-switch.disabled {
  cursor: not-allowed;
  opacity: 0.5;
  background: #F3F4F6;
  border-color: #C9C9C9;
}

.toggle-switch.disabled.active {
  background: #7BA5C9;
  border-color: #7BA5C9;
}

.toggle-handle {
  width: 18px;
  height: 18px;
  background: #374957;
  border-radius: 50%;
  transition: all 0.3s ease;
  transform: translateX(0);
}

.toggle-switch.active .toggle-handle {
  background: #FFFFFF;
  transform: translateX(18px);
}

.toggle-switch.disabled .toggle-handle {
  background: #9CA3AF;
}

.toggle-switch.disabled.active .toggle-handle {
  background: #E5E7EB;
}

/* Draft Placeholder */
.draft-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
  min-height: 300px;
}

.placeholder-icon {
  margin-bottom: 20px;
}

.placeholder-title {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #374957;
  margin: 0 0 12px 0;
}

.placeholder-description {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #6B7280;
  margin: 0;
  max-width: 400px;
}

/* Patients Section */
.patients-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.patients-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 4px;
}

.add-assignee-button {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 4px 12px;
  background: #FFFFFF;
  border: 1px solid #1E4670;
  border-radius: 4px;
  box-shadow: 0px 1px 3px 0px rgba(74, 74, 74, 0.25);
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-assignee-button:hover {
  background: rgba(30, 70, 112, 0.08);
}

.add-assignee-button span {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #1E4670;
}

/* Patient List */
.patient-list {
  height: 324px;
  border-radius: 10px;
  overflow-y: auto;
  padding: 8px 16px;
  margin: 12px 16px 32px 16px;
  background-color: white;
}

.patient-list-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  text-align: center;
}

.empty-icon {
  margin-bottom: 16px;
}

.empty-title {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #4A4A4A;
  margin: 0 0 8px 0;
}

.empty-description {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #6B7280;
  margin: 0;
  max-width: 320px;
}

.patient-list::-webkit-scrollbar {
  width: 6px;
}

.patient-list::-webkit-scrollbar-track {
  background: #F9FBFD;
}

.patient-list::-webkit-scrollbar-thumb {
  background: #C4C4C4;
  border-radius: 3px;
}

.patient-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: #FFFFFF;
  border-radius: 10px;
  border-bottom: 1px solid #C4C4C4;
  transition: all 0.3s ease;
}

/* Transition animations for patient list */
.patient-list-enter-active {
  transition: all 0.3s ease;
}

.patient-list-leave-active {
  transition: all 0.3s ease;
  position: absolute;
  width: calc(100% - 32px);
}

.patient-list-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.patient-list-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.patient-list-move {
  transition: transform 0.3s ease;
}

.patient-avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.patient-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #E8E8E8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  color: #979797;
}

.patient-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.patient-name {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333333;
  margin: 0;
}

.patient-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666666;
}

.patient-id {
  white-space: nowrap;
}

.patient-added {
  white-space: nowrap;
}

.patient-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}


/* Footer */
.dialog-footer {
  display: flex;
  flex-direction: column;
  padding: 0 24px 24px;
  background: #F9FBFD;
  border-radius: 0 0 10px 10px;
}

.footer-actions {
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
  padding-top: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
